import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Matrix } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DemoMedia from "@/components/DemoMedia";
import MatrixDemoAdmin from "@/components/MatrixDemoAdmin";

const SRC = { studio: "Animação MIO Studio · esquema didático", own: "Conteúdo próprio", licensed: "Conteúdo licenciado", ai: "Gerado por IA · revisto" };

function Row({ label, children, testId }) {
  if (!children || (Array.isArray(children) && !children.length)) return null;
  return (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{Array.isArray(children) ? children.join(" · ") : children}</div>
    </div>
  );
}

// Ficha detalhada de um item da Matrix, com a demonstração aprovada mais recente.
export default function MatrixItemSheet({ item, onClose }) {
  const { user } = useAuth();
  const [demos, setDemos] = useState([]);
  const load = () => item && Matrix.demos(item.id).then(setDemos).catch(() => setDemos([]));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [item?.id]);
  const demo = demos[0];
  const forPatient = item?.type === "home";
  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" data-testid="matrix-item-sheet" className="w-full sm:max-w-lg overflow-y-auto bg-slate-950 border-slate-800 text-slate-100 space-y-5">
        {item && <>
          <div className="pr-8">
            <SheetTitle className="font-display text-xl text-slate-50">{item.name}</SheetTitle>
            <SheetDescription className="text-xs text-sky-300 mt-1">{item.category}{item.body_region ? ` · ${item.body_region}` : ""}</SheetDescription>
            <span data-testid="matrix-audience" className={`inline-block mt-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${forPatient ? "border-emerald-500/30 text-emerald-300" : "border-violet-500/30 text-violet-300"}`}>
              {forPatient ? "Adequado ao paciente" : "Uso profissional"}
            </span>
          </div>
          <DemoMedia demo={demo} />
          {demo && <div className="text-[11px] text-slate-500" data-testid="demo-meta">{SRC[demo.kind === "studio" ? "studio" : demo.source]} · v{demo.version}{demo.captions?.["pt-PT"] ? ` — ${demo.captions["pt-PT"]}` : ""}</div>}
          <Row label="Descrição" testId="matrix-description">{item.description}</Row>
          <Row label="Objetivo funcional" testId="matrix-objective">{item.objective || item.therapeutic_goal}</Row>
          <Row label="Instruções de execução" testId="matrix-instructions">{item.instructions !== item.description ? item.instructions : ""}</Row>
          <Row label="Doseamento">{item.repetitions}</Row>
          <Row label="Indicações e precauções" testId="matrix-precautions">{item.contraindications}</Row>
          <Row label="Limitações" testId="matrix-limitations">{item.limitations}</Row>
          <p className="text-[11px] text-slate-500">Conteúdo de apoio à prática profissional; não substitui o raciocínio clínico do osteopata.</p>
          {user?.role === "admin" && <MatrixDemoAdmin item={item} onChanged={load} />}
        </>}
      </SheetContent>
    </Sheet>
  );
}
